export const colors = {
  primary: {
    1: '#EEF4FF',
    2: '#D9E6FF',
    3: '#5994FF',
    4: '#5F8CFF',
    5: '#2D68FF',
    6: '#1653ED',
  },
  secondary: {
    1: '#EDFFF6',
    2: '#D6FFEB',
    3: '#6FE7BF',
    4: '#00BF5F',
    5: '#00A656',
    6: '#067540',
  },
  gradient: {
    linear: 'linear-gradient(158.66deg, #171618 0%, #3B3B3B 100%)',
    platinum: 'linear-gradient(92.99deg, #D5D2DD 0.52%, #FEFCFF 97.59%)',
    vipGold: 'linear-gradient(92.99deg, #D5D2DD 0.52%, #FEFCFF 97.59%)',
    vipDiamond: 'linear-gradient(92.99deg, #D5D2DD 0.52%, #FEFCFF 97.59%)',
    connectedTime: 'linear-gradient(90deg, #00A19B 0%, #9DFCAE 100%)',
  },
  orange: {
    1: '#F8D9BB',
    2: '#FF7D00',
    3: '#DC5500',
    5: '#C84B31',
    6: '#B67650',
  },
  yellow: {
    1: '#FFF7E8',
    2: '#FEEDBF',
    3: '#FBE696',
    4: '#FFDC7C',
    5: '#FDCC43',
    6: '#EBA33B',
    7: '#A95A23',
  },
  purple: {
    1: '#F1EFFF',
    2: '#DAD4FF',
    3: '#9747FF',
    4: '#4F1787',
  },
  other: {
    1: '#FFEAF6',
    2: '#C5282B',
    3: '#F53F3F',
  },
  neutral: {
    text: {
      1: '#FFFFFF',
      2: '#CBD5E1',
      3: '#566A7F',
      4: '#9EA5B1',
      5: '#1D2129',
      6: '#6B7280',
    },
    icon: {
      1: '#E2E5EC',
      2: '#8E9BA9',
      3: '#64748B',
    },
    border: {
      1: '#DADADA',
      2: '#C9CDD4',
      3: '#86909C',
    },
    bg: {
      1: '#FFFFFF',
      2: '#F6F7FC',
      3: '#E3E9ED',
      4: '#D5DDE3',
      5: '#EAEDED',
      6: '#1D2129',
      7: '#777D7C',
    },
  },
  opacity: {
    white: {
      /**
       * White with 40% opacity
       * @color: #FFFFFF
       * @alpha: 40%
       */
      400: '#FFFFFF66',
    },
    black: {
      /**
       * Black with 80% opacity
       * @color: #000000
       * @alpha: 80%
       */
      400: '#000000CC',
      /**
       * Black with 50% opacity
       * @color: #000000
       * @alpha: 50%
       */
      500: '#00000080',
      /**
       * Black with 40% opacity
       * @color: #000000
       * @alpha: 40%
       */
      800: '#00000066',
    },
    blueGray: {
      /**
       * Blue Gray with 90% opacity
       * @color: #131D26
       * @alpha: 90%
       */
      50: '#131D26E5',
      /**
       * Blue Gray with 40% opacity
       * @color: #131D26
       * @alpha: 40%
       */
      400: '#131D2666',
      /**
       * Blue Gray with 5% opacity
       * @color: #131D26
       * @alpha: 5%
       */
      900: '#1E293B0D',
    },
  },
} as const;

export type ColorKey = keyof typeof colors;
export type PrimaryColorKey = keyof typeof colors.primary;
export type SecondaryColorKey = keyof typeof colors.secondary;
export type GradientKey = keyof typeof colors.gradient;
export type OrangeColorKey = keyof typeof colors.orange;
export type YellowColorKey = keyof typeof colors.yellow;
export type PurpleColorKey = keyof typeof colors.purple;
export type OtherColorKey = keyof typeof colors.other;
export type NeutralTextKey = keyof typeof colors.neutral.text;
export type NeutralIconKey = keyof typeof colors.neutral.icon;
export type NeutralBorderKey = keyof typeof colors.neutral.border;
export type NeutralBgKey = keyof typeof colors.neutral.bg;
export type WhiteColorKey = keyof typeof colors.opacity.white;
export type BlackColorKey = keyof typeof colors.opacity.black;
export type BlueGrayColorKey = keyof typeof colors.opacity.blueGray;

export type ColorPath =
  | `primary.${PrimaryColorKey}`
  | `secondary.${SecondaryColorKey}`
  | `gradient.${GradientKey}`
  | `orange.${OrangeColorKey}`
  | `yellow.${YellowColorKey}`
  | `purple.${PurpleColorKey}`
  | `other.${OtherColorKey}`
  | `neutral.text.${NeutralTextKey}`
  | `neutral.icon.${NeutralIconKey}`
  | `neutral.border.${NeutralBorderKey}`
  | `neutral.bg.${NeutralBgKey}`
  | `opacity.white.${WhiteColorKey}`
  | `opacity.black.${BlackColorKey}`
  | `opacity.blueGray.${BlueGrayColorKey}`;

export default colors;
